function normalizeErrorMessage(error) {
  if (typeof error === 'string') {
    return error.trim();
  }

  if (typeof error?.data?.message === 'string') {
    return error.data.message.trim();
  }

  if (typeof error?.message === 'string') {
    return error.message.trim();
  }

  return '';
}

function normalizeOptions(fallbackOrOptions) {
  if (typeof fallbackOrOptions === 'string') {
    return {
      fallbackMessage: fallbackOrOptions,
      messages: {},
    };
  }

  if (fallbackOrOptions && typeof fallbackOrOptions === 'object') {
    return {
      fallbackMessage: fallbackOrOptions.fallbackMessage || '',
      messages: fallbackOrOptions.messages || {},
    };
  }

  return {
    fallbackMessage: '',
    messages: {},
  };
}

const DEFAULT_SAFE_MESSAGES = {
  generic: 'Something went wrong. Please try again.',
  invalidCredentials: 'Email or password is incorrect.',
  accountExists: 'An account already exists for this email.',
  tooManyAttempts: 'Too many attempts. Please wait a moment and try again.',
  passwordsDoNotMatch: 'Passwords do not match.',
  passwordTooShort: 'Password must be at least 6 characters.',
  popupBlocked:
    'Authentication popup was blocked. Please allow popups and try again.',
  googleUnavailable: 'Google sign-in is unavailable right now.',
  googleLoadFailed: 'Google sign-in could not load. Please try again.',
  googleIncomplete: 'Google sign-in did not complete. Please try again.',
  googleFailed: 'Google sign-in failed. Please try again.',
  appleUnavailable: 'Apple sign-in is unavailable right now.',
  appleIncomplete: 'Apple sign-in did not complete. Please try again.',
  appleFailed: 'Apple sign-in failed. Please try again.',
  facebookUnavailable: 'Facebook sign-in is unavailable right now.',
  facebookIncomplete: 'Facebook sign-in did not complete. Please try again.',
  facebookFailed: 'Facebook sign-in failed. Please try again.',
  discordUnavailable: 'Discord sign-in is unavailable right now.',
  discordIncomplete: 'Discord sign-in did not complete. Please try again.',
  discordFailed: 'Discord sign-in failed. Please try again.',
  invalidInviteToken: 'Invalid or expired invite token.',
  invalidResetToken: 'Invalid or expired reset token.',
};

export function getSafeAuthClientMessage(error, fallbackOrOptions) {
  const rawMessage = normalizeErrorMessage(error);
  const status = Number(error?.status || 0);
  const { fallbackMessage, messages } = normalizeOptions(fallbackOrOptions);
  const copy = {
    ...DEFAULT_SAFE_MESSAGES,
    ...messages,
  };

  if (status === 401 || /invalid email or password/i.test(rawMessage)) {
    return copy.invalidCredentials;
  }

  if (
    status === 409 ||
    /already exists|already registered|already in use/i.test(rawMessage)
  ) {
    return copy.accountExists;
  }

  if (status === 429) {
    return copy.tooManyAttempts;
  }

  if (/popup was blocked|allow popups/i.test(rawMessage)) {
    return copy.popupBlocked;
  }

  if (/passwords? do not match/i.test(rawMessage)) {
    return copy.passwordsDoNotMatch;
  }

  if (/at least 6 characters/i.test(rawMessage)) {
    return copy.passwordTooShort;
  }

  if (/invalid or expired invite token/i.test(rawMessage)) {
    return copy.invalidInviteToken;
  }

  if (/invalid or expired reset token/i.test(rawMessage)) {
    return copy.invalidResetToken;
  }

  if (
    /google sign-in is unavailable|not configured|not ready yet/i.test(
      rawMessage,
    )
  ) {
    return copy.googleUnavailable;
  }

  if (/failed to load google sign-in/i.test(rawMessage)) {
    return copy.googleLoadFailed;
  }

  if (/no google credential was returned/i.test(rawMessage)) {
    return copy.googleIncomplete;
  }

  if (/google sign-in failed/i.test(rawMessage)) {
    return copy.googleFailed;
  }

  if (
    /apple sign-in is unavailable|apple sign-in is not configured/i.test(
      rawMessage,
    )
  ) {
    return copy.appleUnavailable;
  }

  if (/apple sign-in did not complete/i.test(rawMessage)) {
    return copy.appleIncomplete;
  }

  if (/apple sign-in failed/i.test(rawMessage)) {
    return copy.appleFailed;
  }

  if (
    /facebook sign-in is unavailable|facebook sign-in is not configured/i.test(
      rawMessage,
    )
  ) {
    return copy.facebookUnavailable;
  }

  if (/facebook sign-in did not complete/i.test(rawMessage)) {
    return copy.facebookIncomplete;
  }

  if (/facebook sign-in failed/i.test(rawMessage)) {
    return copy.facebookFailed;
  }

  if (
    /discord sign-in is unavailable|discord sign-in is not configured/i.test(
      rawMessage,
    )
  ) {
    return copy.discordUnavailable;
  }

  if (/discord sign-in did not complete/i.test(rawMessage)) {
    return copy.discordIncomplete;
  }

  if (/discord sign-in failed/i.test(rawMessage)) {
    return copy.discordFailed;
  }

  return fallbackMessage || copy.generic;
}
